/**
 * Primitives d'interaction déclaratives — cohérent avec data-ha-scope/-bind/-set
 * et les primitives d'animation (typer/counter/reveal/bar).
 *
 * Deux attributs reconnus au scan :
 *
 *   data-ha-toggle[="<selector>"] [data-ha-toggle-class="<cls>"]
 *     Au click, toggle une classe sur une ou plusieurs cibles. Le sélecteur
 *     est CSS standard, avec deux conventions spéciales :
 *       - omis (data-ha-toggle seul) : toggle sur le trigger lui-même
 *       - "closest:.foo" : toggle sur l'ancêtre matchant .foo (utile pour
 *         les submenus dont le trigger est interne)
 *     Plusieurs cibles via virgule : data-ha-toggle="#sidebar, body"
 *     La classe par défaut est `is-open`. Override globale via
 *     data-ha-toggle-class (peut être une liste : "is-open expanded").
 *     Override par-cible via `selector|classes` :
 *       data-ha-toggle="#sidebar|collapsed, body|nav-collapsed"
 *     Mélange possible : la cible sans `|` utilise data-ha-toggle-class
 *     (ou "is-open" par défaut).
 *
 *     Sync aria-expanded automatique si le trigger porte cet attribut.
 *
 *     data-ha-toggle-dismiss="outside" → ferme (retire la classe) au click
 *     hors du premier target. Utile pour dropdowns.
 *     data-ha-toggle-dismiss="inside" → ferme au click sur un descendant
 *     interactif du target (ex: lien dans un mobile menu). Combinable :
 *     "outside inside" pour fermer dans les deux cas.
 *
 *   data-ha-scroll-active[="<linkSelector>"]
 *     Sur un conteneur de liens internes (#section), highlight le lien dont
 *     la section pointée est actuellement dans le viewport (rootMargin
 *     -40% / -55% pour pondérer le centre). Classe ajoutée : `is-active`.
 *     <linkSelector> optionnel pour cibler une sous-liste (défaut: "a[href^='#']").
 *
 * Idempotent : marqueurs data-ha-*-bound. Click delegation document-level
 * pour les toggles (un seul listener global).
 */

import { getAppService, toMap } from "../../../../HA";
import { initFormDirectives } from "./FormDirectives";
import { initStepForms } from "./StepForm";
import { initAutosaves } from "./Autosave";

export { initAutosaves };
import { Pdf } from "../../HTML/Pdf/Pdf";
import { Tag as PreviewTagCtor } from "../../HTML/Tag";

export function onceI(el: Element, marker: string): boolean {
    if (el.hasAttribute(marker)) { return false; }
    el.setAttribute(marker, "1");
    return true;
}

interface ToggleTarget {
    el: HTMLElement;
    classes: string[];
}

function getSelfAndDescendants(root: Element, selector: string): HTMLElement[] {
    const out: HTMLElement[] = [];
    if (root.matches(selector)) {
        out.push(root as HTMLElement);
    }
    root.querySelectorAll(selector).forEach((el) => out.push(el as HTMLElement));
    return out;
}

function animateCounter(el: HTMLElement): void {
    if (!onceI(el, "data-counter-animated")) { return; }
    const target = parseInt(el.dataset.counter || "", 10);
    if (!Number.isFinite(target)) { return; }
    const suffix = el.dataset.suffix || "";
    const durationMs = 1400;
    const startAt = performance.now();
    const step = (now: number) => {
        const k = Math.min(1, (now - startAt) / durationMs);
        const eased = 1 - Math.pow(1 - k, 3);
        el.textContent = String(Math.round(target * eased)) + suffix;
        if (k < 1) {
            requestAnimationFrame(step);
        }
    };
    requestAnimationFrame(step);
}

function triggerRevealAnimations(container: Element): void {
    getSelfAndDescendants(container, "[data-counter]").forEach((el) => animateCounter(el));
    getSelfAndDescendants(container, "[data-bar]").forEach((el) => {
        const value = el.getAttribute("data-bar");
        if (value === null) { return; }
        (el as HTMLElement).style.width = `${value}%`;
    });
}

function parseTyperPhrases(node: HTMLElement): string[] {
    const raw = node.getAttribute("data-ha-typer-phrases")
        || node.getAttribute("data-ha-typer")
        || "";
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "1") { return []; }
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            return parsed.filter((value) => typeof value === "string");
        }
        if (typeof parsed === "string") {
            return [parsed];
        }
    } catch (e) {
        return [trimmed];
    }
    return [];
}

function resolveTargets(trigger: HTMLElement, selector: string | null, defaultClasses: string[]): ToggleTarget[] {
    if (!selector || selector.trim() === "") {
        return [{ el: trigger, classes: defaultClasses }];
    }
    const out: ToggleTarget[] = [];
    selector.split(",").forEach((raw) => {
        const parts = raw.split("|");
        const sel = (parts[0] || "").trim();
        const override = (parts[1] || "").trim();
        if (sel === "") { return; }
        const classes = override !== "" ? override.split(/\s+/) : defaultClasses;
        if (sel.startsWith("closest:")) {
            const target = trigger.closest(sel.substring("closest:".length)) as HTMLElement | null;
            if (target) { out.push({ el: target, classes }); }
        } else {
            document.querySelectorAll(sel).forEach((el) => out.push({ el: el as HTMLElement, classes }));
        }
    });
    return out;
}

function applyToggle(trigger: HTMLElement, force?: boolean): void {
    const selector = trigger.getAttribute("data-ha-toggle");
    const defaultClasses = (trigger.getAttribute("data-ha-toggle-class") || "is-open").trim().split(/\s+/);
    const targets = resolveTargets(trigger, selector, defaultClasses);
    if (targets.length === 0) { return; }
    const first = targets[0];
    let newState = force ?? !first.el.classList.contains(first.classes[0]);
    targets.forEach((t) => {
        t.classes.forEach((c) => {
            if (newState) { t.el.classList.add(c); } else { t.el.classList.remove(c); }
        });
    });
    if (trigger.hasAttribute("aria-expanded")) {
        trigger.setAttribute("aria-expanded", String(newState));
    }
    const dismiss = trigger.getAttribute("data-ha-toggle-dismiss") || "";
    if (newState && dismiss.includes("outside")) { bindOutsideDismiss(trigger, first.el); }
    if (newState && dismiss.includes("inside")) { bindInsideDismiss(trigger, first.el); }
}

const insideDismissAttached = new WeakSet<HTMLElement>();
function bindInsideDismiss(trigger: HTMLElement, target: HTMLElement): void {
    if (insideDismissAttached.has(target)) { return; }
    insideDismissAttached.add(target);
    target.addEventListener("click", (e) => {
        const t = e.target as HTMLElement;
        if (!t || t === target) { return; }
        // Ne ferme que sur un descendant interactif (<a>, <button>, [data-ha-set]…),
        // pour ne pas fermer accidentellement quand l'utilisateur clique
        // un élément non-actionnable du panneau.
        const closestInteractive = t.closest("a, button, [data-ha-set], [data-ha-toggle]");
        if (!closestInteractive || closestInteractive === trigger) { return; }
        applyToggle(trigger, false);
    });
}

let outsideDismissBound = false;
const dismissPairs: Array<{ trigger: HTMLElement, target: HTMLElement }> = [];

function bindOutsideDismiss(trigger: HTMLElement, target: HTMLElement): void {
    if (!dismissPairs.some((p) => p.trigger === trigger)) {
        dismissPairs.push({ trigger, target });
    }
    if (outsideDismissBound) { return; }
    outsideDismissBound = true;
    document.addEventListener("click", (e) => {
        const t = e.target as Node;
        dismissPairs.forEach((pair) => {
            if (!pair.target.classList.contains((pair.trigger.getAttribute("data-ha-toggle-class") || "is-open").split(/\s+/)[0])) { return; }
            if (pair.trigger.contains(t) || pair.target.contains(t)) { return; }
            applyToggle(pair.trigger, false);
        });
    }, true);
}

let toggleListenerBound = false;
export function bindToggleListener(): void {
    if (toggleListenerBound) { return; }
    toggleListenerBound = true;
    document.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        if (!target || !target.closest) { return; }
        const trigger = target.closest("[data-ha-toggle]") as HTMLElement;
        if (!trigger) { return; }
        // Skip if the click already handled by a Scope setter
        // (data-ha-set takes precedence within a scope tree).
        if (trigger.hasAttribute("data-ha-set")) { return; }
        e.preventDefault();
        applyToggle(trigger);
    });
}

/**
 * Navigation SPA des liens `a[data-app]` de la nav publique (ha/443b).
 *
 * Anchor n'installe pas son handler quand `data-app` est présent (soft-handoff
 * vers « le controller spécifique »). Le seul controller qui interceptait
 * `a[data-app]` était DashboardNavigation, scopé `#main-sidebar` → la nav
 * publique (`#ha-topnav`) tombait sur le `href` = full reload. Ce handler global
 * couvre tous les `a[data-app]` HORS `#main-sidebar` (déféré à DashboardNavigation)
 * et dispatch un `showApp` client-side (pas de reload). Le `href` reste le
 * fallback si le service n'est pas dispo.
 */
let spaNavLinksBound = false;
export function bindSpaNavLinks(): void {
    if (spaNavLinksBound) { return; }
    spaNavLinksBound = true;
    document.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        if (!target || !target.closest) { return; }
        const link = target.closest("a[data-app]") as HTMLAnchorElement | null;
        if (!link) { return; }
        if (link.closest("#main-sidebar")) { return; }   // géré par DashboardNavigation
        const me = e as MouseEvent;
        if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) { return; }
        const pageId = link.getAttribute("data-app") || "";
        if (!pageId) { return; }
        // Une query string ne survit pas au showApp SPA (le pageId ne transporte
        // pas de paramètres, et le gate serveur doit voir l'URL complète pour la
        // capture post-login) : navigation pleine page via le href.
        const href = link.getAttribute("href") || "";
        if (href.indexOf("?") >= 0) { return; }
        const svc = getAppService();
        if (!svc) { return; }                            // laisse le href faire le fallback
        e.preventDefault();
        svc.processAction(toMap({ t: "showApp", id: pageId }) as Map<any, any>);
    });
}

/**
 * data-ha-template-pick="<key>" — pills de templates pré-rédigés (ha/525).
 *
 * Au click sur une pill, échange l'objet et le message du form contenant :
 *   - lit `data-tpl-subject` / `data-tpl-body` sur la pill (posés côté serveur
 *     par ShowDevis::hydrateTemplatePills, per-devis) ;
 *   - écrit dans le champ objet (premier `input[type=text]`) et le champ message
 *     (premier `textarea`) du form ancêtre. o1x retire `name`/`id` des inputs au
 *     rendu : on cible donc par type DOM. Le groupe peut surcharger les cibles via
 *     `data-ha-template-subject="<sel>"` / `data-ha-template-body="<sel>"` ;
 *   - bascule la classe `active` parmi les pills du `[data-ha-template-group]`.
 *
 * Remplace l'ancien handler inline du mockup (jamais bindé après le rendu
 * JSON→DOM du shell). Délégation document-level, un seul listener global.
 */
let templatePickerBound = false;
export function bindTemplatePicker(): void {
    if (templatePickerBound) { return; }
    templatePickerBound = true;
    document.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        if (!target || !target.closest) { return; }
        const pill = target.closest("[data-ha-template-pick]") as HTMLElement | null;
        if (!pill) { return; }
        e.preventDefault();

        const group = pill.closest("[data-ha-template-group]") as HTMLElement | null;
        const form = pill.closest("form");
        if (form) {
            const subject = pill.getAttribute("data-tpl-subject");
            const body = pill.getAttribute("data-tpl-body");
            const subjectSel = (group && group.getAttribute("data-ha-template-subject")) || 'input[type="text"]';
            const bodySel = (group && group.getAttribute("data-ha-template-body")) || "textarea";
            const subjectEl = form.querySelector(subjectSel) as HTMLInputElement | HTMLTextAreaElement | null;
            const bodyEl = form.querySelector(bodySel) as HTMLInputElement | HTMLTextAreaElement | null;
            if (subjectEl && subject !== null) { subjectEl.value = subject; }
            if (bodyEl && body !== null) { bodyEl.value = body; }
        }

        if (group) {
            group.querySelectorAll("[data-ha-template-pick]").forEach((p) => {
                p.classList.toggle("active", p === pill);
            });
        }
    });
}

export function initToggles(root: ParentNode = document): void {
    bindToggleListener();
    const triggers = root.querySelectorAll("[data-ha-toggle]:not([data-ha-toggle-bound])");
    triggers.forEach((node) => {
        if (!onceI(node, "data-ha-toggle-bound")) { return; }
        const trigger = node as HTMLElement;
        if (trigger.hasAttribute("aria-expanded")) {
            const defaultClasses = (trigger.getAttribute("data-ha-toggle-class") || "is-open").split(/\s+/);
            const selector = trigger.getAttribute("data-ha-toggle");
            const first = resolveTargets(trigger, selector, defaultClasses)[0];
            if (first) {
                trigger.setAttribute("aria-expanded", String(first.el.classList.contains(first.classes[0])));
            }
        }
    });
}

export function initScrollActives(root: ParentNode = document): void {
    const nodes = root.querySelectorAll("[data-ha-scroll-active]:not([data-ha-scroll-active-bound])");
    nodes.forEach((node) => {
        if (!onceI(node, "data-ha-scroll-active-bound")) { return; }
        const navEl = node as HTMLElement;
        // Le converter mockup sérialise les attributs HTML vides en "1"
        // (boolean truthy). On vérifie la validité du selector via
        // try/catch : "1" planterait querySelectorAll.
        const rawSel = navEl.getAttribute("data-ha-scroll-active") || "";
        const defaultSel = "a[href^='#']";
        let links: HTMLAnchorElement[];
        try {
            links = Array.from(
                navEl.querySelectorAll(rawSel === "" || rawSel === "1" ? defaultSel : rawSel)
            ) as HTMLAnchorElement[];
        } catch (e) {
            links = Array.from(navEl.querySelectorAll(defaultSel)) as HTMLAnchorElement[];
        }
        const sectionByLink = new Map<HTMLAnchorElement, Element>();
        links.forEach((l) => {
            const href = l.getAttribute("href") || "";
            if (!href.startsWith("#") || href === "#") { return; }
            const section = document.querySelector(href);
            if (section) { sectionByLink.set(l, section); }
        });
        if (sectionByLink.size === 0) { return; }
        const obs = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) { return; }
                const id = "#" + (entry.target as HTMLElement).id;
                links.forEach((l) => {
                    l.classList.toggle("is-active", l.getAttribute("href") === id);
                });
            });
        }, { rootMargin: "-40% 0px -55% 0px" });
        sectionByLink.forEach((section) => obs.observe(section));
    });
}

/**
 * data-ha-nav-active — item de nav actif par ROUTE (nav multi-page, ≠ scroll-spy).
 *
 * Sur le conteneur nav : marque `.is-current` + `aria-current="page"` le lien dont
 * le href correspond à la route courante, retire des autres. Selector des liens
 * configurable (`data-ha-nav-active="<sel>"`, défaut `a[href]`).
 *
 * PAS de garde once : ré-évalué à CHAQUE appel (mount + MutationObserver sur swap
 * SPA) — l'item actif suit la navigation sans reload. Idempotent (clear + set).
 * Résout le header partagé (`site/header`) dont le `is-current` SSR est figé sur
 * la page propriétaire.
 */
export function initNavActive(root: ParentNode = document): void {
    const path = normalizeNavPath(typeof location !== "undefined" ? location.pathname : "/");
    root.querySelectorAll("[data-ha-nav-active]").forEach((node) => {
        const navEl = node as HTMLElement;
        const rawSel = navEl.getAttribute("data-ha-nav-active") || "";
        const sel = rawSel === "" || rawSel === "1" ? "a[href]" : rawSel;
        let links: HTMLAnchorElement[];
        try {
            links = Array.from(navEl.querySelectorAll(sel)) as HTMLAnchorElement[];
        } catch {
            links = Array.from(navEl.querySelectorAll("a[href]")) as HTMLAnchorElement[];
        }
        links.forEach((l) => {
            const href = normalizeNavPath(l.getAttribute("href") || "");
            const match = href !== "" && href === path;
            l.classList.toggle("is-current", match);
            if (match) {
                l.setAttribute("aria-current", "page");
            } else {
                l.removeAttribute("aria-current");
            }
        });
    });
}

/** Chemin comparable : sans origin/query/hash, sans slash final (hors racine). */
function normalizeNavPath(raw: string): string {
    let s = raw.split("?")[0].split("#")[0];
    // href absolu (https://…/x) → garder le pathname
    const schemeIdx = s.indexOf("://");
    if (schemeIdx !== -1) {
        const rest = s.slice(schemeIdx + 3);
        const slash = rest.indexOf("/");
        s = slash === -1 ? "/" : rest.slice(slash);
    }
    if (s.length > 1) {
        s = s.replace(/\/+$/, "");
    }
    return s === "" ? "/" : s;
}

/**
 * data-ha-reveal — ajoute `.is-revealed` à l'entrée dans le viewport puis
 * déclenche les descendants `[data-counter]` / `[data-bar]` une seule fois.
 */
export function initReveals(root: ParentNode = document): void {
    const nodes = root.querySelectorAll("[data-ha-reveal]:not([data-ha-reveal-bound])");
    nodes.forEach((node) => {
        if (!onceI(node, "data-ha-reveal-bound")) { return; }
        const el = node as HTMLElement;
        const obs = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) { return; }
                const target = entry.target as HTMLElement;
                target.classList.add("is-revealed");
                triggerRevealAnimations(target);
                observer.unobserve(target);
            });
        }, { threshold: 0.2 });
        obs.observe(el);
    });
}

/**
 * data-ha-carousel — rotation automatique de slides + dots cliquables +
 * caption synchronisée + pause au hover et quand l'onglet est caché.
 *
 * Structure HTML attendue (les slides sont en SSR statique) :
 *
 *   <div class="hero-visual" data-ha-reveal>
 *     <div class="carousel-track" data-ha-carousel data-ha-carousel-interval="5000">
 *       <div class="carousel-slide is-active" data-title="..." data-sub="..."
 *            role="tabpanel" aria-hidden="false">
 *         <img src="..." alt="..." loading="eager">
 *       </div>
 *       <div class="carousel-slide" data-title="..." data-sub="..."
 *            role="tabpanel" aria-hidden="true">
 *         <img src="..." alt="..." loading="lazy">
 *       </div>
 *       …
 *     </div>
 *     <div class="carousel-overlay"></div>
 *     <div class="carousel-caption"></div>  ← rempli à chaque changement
 *     <div class="carousel-dots"></div>     ← un bouton généré par slide
 *   </div>
 *
 * - L'élément avec `data-ha-carousel` est le track contenant les slides
 * - `data-ha-carousel-interval` (ms, défaut 5000) pilote la rotation
 * - `.carousel-caption` et `.carousel-dots` sont cherchés dans le parent
 *   commun (premier ancêtre contenant les 3 éléments)
 */
export function initCarousels(root: ParentNode = document): void {
    const nodes = root.querySelectorAll("[data-ha-carousel]:not([data-ha-carousel-bound])");
    nodes.forEach((node) => {
        if (!onceI(node, "data-ha-carousel-bound")) { return; }
        const track = node as HTMLElement;
        const slides = Array.from(track.querySelectorAll(".carousel-slide")) as HTMLElement[];
        if (slides.length === 0) { return; }
        const wrapper = track.parentElement || track;
        const dotsEl = wrapper.querySelector(".carousel-dots") as HTMLElement | null;
        const captionEl = wrapper.querySelector(".carousel-caption") as HTMLElement | null;
        const interval = parseInt(track.getAttribute("data-ha-carousel-interval") || "5000", 10) || 5000;

        // Build dots if container exists and empty.
        if (dotsEl && dotsEl.children.length === 0) {
            slides.forEach((_, i) => {
                const b = document.createElement("button");
                b.type = "button";
                b.setAttribute("role", "tab");
                b.setAttribute("aria-label", `Visuel ${i + 1}`);
                b.dataset.i = String(i);
                if (i === 0) { b.classList.add("is-active"); }
                dotsEl.appendChild(b);
            });
        }
        const dots = dotsEl ? Array.from(dotsEl.querySelectorAll("button")) as HTMLButtonElement[] : [];

        const setCaption = (i: number) => {
            if (!captionEl) { return; }
            const s = slides[i];
            const title = s.getAttribute("data-title") || "";
            const sub = s.getAttribute("data-sub") || "";
            captionEl.innerHTML = `<div class="ttl">${escapeHtml(title)}</div><div class="sub">${escapeHtml(sub)}</div>`;
        };
        setCaption(0);

        let idx = 0;
        let timer: number | null = null;
        const go = (n: number) => {
            slides[idx].classList.remove("is-active");
            slides[idx].setAttribute("aria-hidden", "true");
            if (dots[idx]) { dots[idx].classList.remove("is-active"); }
            idx = ((n % slides.length) + slides.length) % slides.length;
            slides[idx].classList.add("is-active");
            slides[idx].setAttribute("aria-hidden", "false");
            if (dots[idx]) { dots[idx].classList.add("is-active"); }
            setCaption(idx);
        };
        const start = () => { if (timer === null) { timer = window.setInterval(() => go(idx + 1), interval); } };
        const stop = () => { if (timer !== null) { window.clearInterval(timer); timer = null; } };

        dots.forEach((d) => {
            d.addEventListener("click", () => {
                go(parseInt(d.dataset.i || "0", 10));
                stop(); start();
            });
        });
        wrapper.addEventListener("mouseenter", stop);
        wrapper.addEventListener("mouseleave", start);
        document.addEventListener("visibilitychange", () => { document.hidden ? stop() : start(); });
        start();
    });
}

function escapeHtml(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * data-ha-marquee — duplique les enfants pour donner l'illusion d'une boucle
 * infinie via animation CSS `@keyframes marquee` (animation pilotée par le
 * CSS du mockup, ici on duplique juste le contenu).
 *
 * Idempotent : marqueur data-ha-marquee-bound + on n'ajoute le clone qu'une
 * seule fois.
 */
export function initMarquees(root: ParentNode = document): void {
    const nodes = root.querySelectorAll("[data-ha-marquee]:not([data-ha-marquee-bound])");
    nodes.forEach((node) => {
        if (!onceI(node, "data-ha-marquee-bound")) { return; }
        const el = node as HTMLElement;
        // Clone children once for the seamless loop effect (CSS animation
        // typically translates -50% so the second copy aligns naturally).
        const originals = Array.from(el.children);
        originals.forEach((child) => {
            el.appendChild(child.cloneNode(true));
        });
    });
}

/**
 * data-ha-typer / data-ha-typer-phrases — phrases JSON animées en boucle
 * avec effet machine à écrire (typing / deleting).
 */
export function initTypers(root: ParentNode = document): void {
    const nodes = root.querySelectorAll("[data-ha-typer]:not([data-ha-typer-bound]), [data-ha-typer-phrases]:not([data-ha-typer-bound])");
    nodes.forEach((node) => {
        if (!onceI(node, "data-ha-typer-bound")) { return; }
        const el = node as HTMLElement;
        const phrases = parseTyperPhrases(el);
        if (phrases.length === 0) { return; }
        let phraseIndex = 0;
        let charIndex = 0;
        let deleting = false;
        const tick = () => {
            const full = phrases[phraseIndex] || "";
            if (deleting) {
                charIndex = Math.max(0, charIndex - 1);
            } else {
                charIndex = Math.min(full.length, charIndex + 1);
            }
            el.textContent = full.substring(0, charIndex);
            let delay = deleting ? 35 : 75;
            if (!deleting && charIndex === full.length) {
                delay = 1800;
                deleting = true;
            } else if (deleting && charIndex === 0) {
                deleting = false;
                phraseIndex = (phraseIndex + 1) % phrases.length;
                delay = 350;
            }
            window.setTimeout(tick, delay);
        };
        tick();
    });
}

/**
 * data-ha-track — beacon d'interaction par publication (concept newsroom lld).
 *
 *   <div data-ha-track="view" [data-ha-track-id="HA…"] [data-ha-track-e="Article"] hidden></div>
 *
 * Au montage de l'élément : POST /ha/i (append-only côté serveur, agrégé par
 * ScheduledJob). L'id par défaut est extrait du pathname (/a/<HAId>…) — la
 * page n'a rien à lier. Dédup par montage : le marqueur data-ha-track-bound
 * garantit UN beacon par rendu de page (la nav SPA reconstruit le DOM → un
 * nouveau beacon, correct). fetch keepalive + token CSRF de la meta : survit
 * à la navigation, ne casse jamais la page.
 */
const TRACK_TYPES = new Set(["view", "share"]);

export function initTrackBeacons(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>("[data-ha-track]:not([data-ha-track-bound])").forEach((el) => {
        if (!onceI(el, "data-ha-track-bound")) { return; }
        const type = el.getAttribute("data-ha-track") || "view";
        if (!TRACK_TYPES.has(type)) { return; }
        const id = el.getAttribute("data-ha-track-id")
            || (window.location.pathname.match(/HA\d{10}[\w.]*/) || [])[0];
        if (!id) { return; }
        const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
        const body = new URLSearchParams({ id, t: type });
        const entity = el.getAttribute("data-ha-track-e");
        if (entity) { body.set("e", entity); }
        fetch("/ha/i", {
            method: "POST",
            keepalive: true,
            credentials: "same-origin",
            headers: {
                "X-CSRF-Token": csrf,
                "X-Requested-With": "XMLHttpRequest",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body,
        }).catch(() => { /* le tracking ne remonte jamais d'erreur au visiteur */ });
    });
}

/**
 * data-ha-blocks — palette d'insertion de blocs HADown (éditeur newsroom).
 *
 *   <div data-ha-blocks="body">
 *     <button type="button" data-block="img">Image</button> …
 *   </div>
 *
 * Chaque bouton insère le squelette du bloc au curseur du textarea dont le
 * `name` correspond à la valeur de data-ha-blocks (dans le même <form>).
 */
const BLOCK_SNIPPETS: Record<string, string> = {
    img:     "##img\nHA_ID_DU_MEDIA\n",
    youtube: "##youtube\nID_VIDEO\n",
    twitter: "##twitter\nURL_DU_TWEET\n",
    cite:    "##cite\n#t=Source\nTexte de la citation\n",
    ref:     "##ref\nHA_ID_ARTICLE\n",
    refd:    "##refd\nHA_ID_DOCUMENT\n",
    gal:     "##gal\n#n=Galerie\nHA_ID_MEDIA_1\nHA_ID_MEDIA_2\n",
    table:   "##table\ncolonne 1 | colonne 2\nval 1 | val 2\n",
    code:    "##code\n#t=Titre\ncontenu\n",
    t2:      "##t2\nIntertitre\n",
    t3:      "##t3\nSous-titre\n",
};

export function initBlockPalettes(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>("[data-ha-blocks]:not([data-ha-blocks-bound])").forEach((palette) => {
        if (!onceI(palette, "data-ha-blocks-bound")) { return; }
        const targetName = palette.getAttribute("data-ha-blocks") || "body";
        palette.addEventListener("click", (ev) => {
            const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-block]");
            if (!btn) { return; }
            ev.preventDefault();
            const snippet = BLOCK_SNIPPETS[btn.getAttribute("data-block") || ""];
            const area = palette.closest("form")?.querySelector<HTMLTextAreaElement>(`textarea[name="${targetName}"]`);
            if (!snippet || !area) { return; }
            const start = area.selectionStart ?? area.value.length;
            const end = area.selectionEnd ?? start;
            const before = area.value.slice(0, start);
            const after = area.value.slice(end);
            // bloc = paragraphe : garantir la ligne vide de séparation
            const pre = before === "" || before.endsWith("\n\n") ? "" : (before.endsWith("\n") ? "\n" : "\n\n");
            area.value = before + pre + snippet + (after.startsWith("\n") ? "" : "\n") + after;
            const pos = (before + pre + snippet).length;
            area.focus();
            area.setSelectionRange(pos, pos);
            area.dispatchEvent(new Event("input", { bubbles: true }));
        });
    });
}

/**
 * data-ha-filter — filtre client d'une liste par texte.
 *
 *   <input data-ha-filter=".nre-media-grid label" placeholder="Filtrer…">
 *
 * Masque (hidden) les cibles dont le textContent ne contient pas la saisie.
 */
export function initFilters(root: ParentNode = document): void {
    root.querySelectorAll<HTMLInputElement>("input[data-ha-filter]:not([data-ha-filter-bound])").forEach((input) => {
        if (!onceI(input, "data-ha-filter-bound")) { return; }
        const selector = input.getAttribute("data-ha-filter") || "";
        input.addEventListener("input", () => {
            const needle = input.value.trim().toLowerCase();
            document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
                el.hidden = needle !== "" && !(el.textContent || "").toLowerCase().includes(needle);
            });
        });
    });
}

/**
 * data-ha-preview — aperçu WYSIWYG du corps HADown (éditeur newsroom).
 *
 *   <button type="button" data-ha-preview="body" data-ha-preview-target="#a-preview"
 *           data-ha-preview-form="ile.nr.article.preview">Aperçu</button>
 *
 * Au clic : POST /ha/form (formId = data-ha-preview-form, route de l'app qui sert
 * la page) avec le contenu du textarea cible → le serveur rend l'arbre bodyF (même
 * moteur que le détail public) → monté dans le panneau via le constructeur de Tag o1x.
 * Attribut absent ou blanc : aucun POST, le panneau le dit (HA-134 — un id d'app en
 * dur visait la route lld depuis ILEIRA).
 */
export function initPreviews(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>("[data-ha-preview]:not([data-ha-preview-bound])").forEach((btn) => {
        if (!onceI(btn, "data-ha-preview-bound")) { return; }
        btn.addEventListener("click", async () => {
            const areaName = btn.getAttribute("data-ha-preview") || "body";
            const targetSel = btn.getAttribute("data-ha-preview-target") || "#a-preview";
            const area = btn.closest("form")?.querySelector<HTMLTextAreaElement>(`textarea[name="${areaName}"]`);
            const panel = document.querySelector<HTMLElement>(targetSel);
            if (!area || !panel) { return; }
            const formId = (btn.getAttribute("data-ha-preview-form") || "").trim();
            if (formId === "") {
                panel.hidden = false;
                panel.textContent = "Aperçu non configuré.";
                return;
            }
            const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
            const body = new URLSearchParams({ formId, body: area.value });
            try {
                const resp = await fetch("/ha/form", {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        "X-CSRF-Token": csrf,
                        "X-Requested-With": "XMLHttpRequest",
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body,
                });
                const json = await resp.json();
                panel.hidden = false;
                panel.innerHTML = "";
                if (json && json.ok && json.tree) {
                    const tag = new PreviewTagCtor(toMap(json.tree) as any);
                    panel.appendChild(tag.getElement());
                } else {
                    panel.textContent = (json && (json.message || json.msg)) || "Aperçu indisponible.";
                }
            } catch (e) {
                panel.hidden = false;
                panel.textContent = "Aperçu indisponible.";
            }
        });
    });
}

let interactionsMutationObs: MutationObserver | null = null;

/**
 * Orchestrator pour les primitives d'interaction. Appelée par WebPage.start
 * + ré-appelée sur app change (via subscribeToAppInfo) pour rebinder les
 * triggers injectés dans les nouveaux trees.
 */
export function initInteractions(root: ParentNode = document): void {
    bindSpaNavLinks();
    bindTemplatePicker();
    initToggles(root);
    initScrollActives(root);
    initNavActive(root);
    initReveals(root);
    initCarousels(root);
    initMarquees(root);
    initTypers(root);
    initFormDirectives(root);
    initStepForms(root);
    initTrackBeacons(root);
    initBlockPalettes(root);
    initAutosaves(root);
    initFilters(root);
    initPreviews(root);
    Pdf.bindDelegation();

    if (root === document && !interactionsMutationObs && typeof MutationObserver !== "undefined") {
        interactionsMutationObs = new MutationObserver(() => {
            initToggles();
            initScrollActives();
            initNavActive();
            initReveals();
            initCarousels();
            initMarquees();
            initTypers();
            initFormDirectives();
            initStepForms();
            initTrackBeacons();
            initBlockPalettes();
            initAutosaves();
            initFilters();
            initPreviews();
        });
        interactionsMutationObs.observe(document.body, { childList: true, subtree: true });
    }
}
